/**
 * Token persistence. Keys kept compatible with datacentral-client-v4 so an
 * existing session in the same browser keeps working:
 *   SA   = access token
 *   SR   = refresh token
 *   EXP  = access-token expiry as an absolute UNIX timestamp in **seconds**
 *   UUID = user id
 * Sessions here are Keycloak-issued (realm `internal`).
 */
const KEY = {
  access: 'SA',
  refresh: 'SR',
  expiry: 'EXP',
  uuid: 'UUID',
  authMethod: 'AUTH_METHOD',
} as const;

export type AuthMethod = 'sso' | 'google';

export interface SetTokenInput {
  access: string;
  refresh?: string;
  /** Absolute expiry as a UNIX timestamp in seconds (the token's `exp`). */
  expSeconds?: number;
  uuid?: string;
  authMethod?: AuthMethod;
}

export const tokenStorage = {
  get access() {
    return localStorage.getItem(KEY.access);
  },
  get refresh() {
    return localStorage.getItem(KEY.refresh);
  },
  /** Expiry as absolute UNIX seconds, or null if unknown. */
  get expSeconds(): number | null {
    const v = localStorage.getItem(KEY.expiry);
    return v ? Number(v) : null;
  },
  get uuid() {
    return localStorage.getItem(KEY.uuid);
  },
  get authMethod(): AuthMethod {
    const v = localStorage.getItem(KEY.authMethod);
    return v === 'google' ? 'google' : 'sso';
  },
  get isExpired(): boolean {
    const exp = this.expSeconds;
    if (!exp) return true;
    return Date.now() / 1000 >= exp;
  },
  set({ access, refresh, expSeconds, uuid, authMethod }: SetTokenInput) {
    localStorage.setItem(KEY.access, access);
    if (refresh) localStorage.setItem(KEY.refresh, refresh);
    if (expSeconds != null) localStorage.setItem(KEY.expiry, String(expSeconds));
    if (uuid) localStorage.setItem(KEY.uuid, uuid);
    if (authMethod) localStorage.setItem(KEY.authMethod, authMethod);
  },
  clear() {
    Object.values(KEY).forEach((k) => localStorage.removeItem(k));
    // Drop any in-flight SSO handshake state so an aborted login or a logout
    // doesn't leak the PKCE verifier / intended URL into the next session.
    sessionStorage.removeItem('pkce_code_verifier');
    sessionStorage.removeItem('intended_url');
  },
};
